import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ADMIN } from "./_shared.js";

/**
 * `MEMBER-SCAN` (@UC0216) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/members` (ADR-0009 bất biến 5).
 * US-AIV2-0110 S10 (`@UC0259`): trước trỏ trang chủ nhóm `/groups/:groupId`; nay tab "Thành viên" — màn chi tiết thành viên cần `memberId` mà engine chưa điền được.
 * Cửa mobile trang này chịu trách nhiệm (chat không có ô đính kèm file (QĐ PO 17/09 mục 3) ⇒ quét ảnh là việc của GUI).
 * 🔴 GUIDE-SCREEN-TRUTH (lô review 15/10 G-1) — cập nhật @UC0340 (QĐ người dùng 18/10/2026): tab ảnh («Nhập tự động») nay có ở CẢ HAI loại
 * nhóm — nhóm thường tới nó qua «Thêm tên người chưa có app» (sheet `namesOnly`, chỉ lấy tên). "Dán danh sách trong chat" ở nhóm thường VẪN
 * bị `ONB-ADD-MEMBERS` từ chối (`GROUP_SEATS_PARENT_ONLY` — QĐ 0116 §7, không đổi). Trang redirect không có cơ chế câu theo loại nhóm ⇒ MỘT câu đúng cho cả hai loại + đích là route
 * `/groups/:groupId/add-member` (app TỰ rẽ theo loại nhóm: phụ huynh ⇒ sheet có tab ảnh · nhóm khác ⇒ bảng chọn cách thêm) — cùng route
 * `guiScreen` của `ONB-ADD-MEMBERS`, không route mới.

 * 🔴 Vai HẸP HƠN cảm giác ban đầu (US-AIV2-0049, review #827 H1 — ĐO từ guard thật, đừng nới lại):
 *   `POST /groups/{groupId}/members/ocr-scan` gác `@RequireGroupRoles(OWNER, ADMIN)` — mirror `createMemberSeats`.
 *   Trang redirect chỉ mở màn hình, nên mời sai vai = dẫn người ta tới nút bấm vào là 403
 *   (CLAUDE.md §4 cấm: "dẫn member thường vào form quản trị rồi để 403 ở bước chốt").
 *   Khoá bằng `roles-parity.invariants.spec.ts` — nới ở đây mà GUI không nới thì test ĐỎ.
 */
export const MEMBER_SCAN = {
  id: "MEMBER-SCAN",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  roles: OWNER_ADMIN,
  flags: [],
  slots: [],
  llm: {
    description: "Quét danh sách thành viên từ ảnh chụp hoặc file để thêm nhiều người một lượt.",
    examples: [
      "quét danh sách từ ảnh",
      "quet danh sach tu anh",
      "chụp danh sách lớp để thêm",
      "import excel danh sách",
      "ocr danh sách",
      // ——— 0105: thêm cho đủ 40 ———
      "chup danh sach lop de them",
      "import excel danh sach",
      "ocr danh sach",
      "quét ảnh danh sách phụ huynh",
      "quet anh danh sach phu huynh",
      "tôi có ảnh danh sách, quét vào được không?",
      "toi co anh danh sach quet vao duoc khong",
      "nhập danh sách từ file excel",
      "nhap danh sach tu file excel",
      "chụp hình sổ điểm danh để thêm người",
      "chup hinh so diem danh de them nguoi",
      "quét file danh sách thành viên",
      "quet file danh sach thanh vien",
      "có cách nào scan danh sách không",
      "co cach nao scan danh sach khong",
      "đọc ảnh danh sách rồi thêm vào nhóm",
      "doc anh danh sach roi them vao nhom",
      "upload file danh sách",
      "upload file danh sach",
      "tôi chụp màn hình danh sách, nhận diện giúp",
      "toi chup man hinh danh sach nhan dien giup",
      "quét ảnh để thêm nhiều người một lượt",
      "quet anh de them nhieu nguoi mot luot",
      "scan danh sách lớp",
      "scan danh sach lop",
      "nhận diện tên từ ảnh",
      "nhan dien ten tu anh",
      "import danh sách từ ảnh chụp",
      "import danh sach tu anh chup",
      "chụp danh sách đội bóng để nhập",
      "chup danh sach doi bong de nhap",
      "quét ảnh danh sách",
      "có quét được file không",
      "co quet duoc file khong",
      "dùng ảnh để thêm thành viên",
      "dung anh de them thanh vien",
    ],
  },
  screen: "/groups/:groupId/add-member",
  screenShows: "member_scan",
  endpoints: [
    "POST /groups/{groupId}/members/ocr-scan",
  ],
  facts: [],
  templates: {
    redirect:
      "Mình mở bước thêm thành viên nhé. Chọn «Nhập tự động» để chụp hoặc tải ảnh danh sách — nhóm khác nhóm phụ huynh thì chọn «Thêm tên người chưa có app» trước, máy chỉ đọc tên. Ở nhóm phụ huynh bạn cũng dán được danh sách vào chat, mỗi dòng một người.",
  },
  // `help.title` ("Quét danh sách từ ảnh") vào chỉ mục tìm trang ⇒ giữ; nút mở màn nói đúng việc ở MỌI loại nhóm.
  screenLabel: "Thêm thành viên",
  journey: { after: ["ONB-ADD-MEMBERS"] },
  help: { title: "Quét danh sách từ ảnh", tryNow: "quét danh sách từ ảnh" },
} satisfies AiCapabilityPage;
